import {ApplicationRef, ComponentFactoryResolver, ComponentRef, EmbeddedViewRef, Inject, Injectable, Injector} from "@angular/core";
import {ReplaySubject} from "rxjs";
import {DOCUMENT} from "@angular/common";
import {ENVIRONMENT, IS_BROWSER} from "../utils/injectiontokens";
import { RecaptchaPopupComponent } from "./recaptchapopup/recaptchapopup.component";

declare var grecaptcha: any;

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {

  public recaptchaScore = 1; //by default, no suspicious
  public recaptchaScore$ = new ReplaySubject<number>(null);
  public recaptchaLoaded$ = new ReplaySubject<boolean>(null);
  public token = null;
  public token$ = new ReplaySubject<number>(null);
  public tokenTimestamp = null;
  
  protected componentRef: ComponentRef<RecaptchaPopupComponent> = null;

  constructor(@Inject(DOCUMENT) private document: Document,
              @Inject(ENVIRONMENT) protected environment: any,
              protected componentFactoryResolver: ComponentFactoryResolver,
              protected appRef: ApplicationRef,
              protected injector: Injector,              
              @Inject(IS_BROWSER) protected isBrowser: boolean) {

  }

  public generateNewRecaptchaToken() {
    let returnValue = new Promise((resolve, reject) => {
      grecaptcha.execute(this.environment.reCAPTCHA3SiteKey).then((token) => {
        //store the timestamp of the token generation
        this.tokenTimestamp = new Date().getTime();
        this.token = token;
        this.token$.next(token);
        resolve(token);
      },
      (error) => {
        reject(error);
      });
    });
    return returnValue;
  }

  public hasExpiredRecaptchaToken() {
    //check if the token timestamp is older than 2 minutes
    if(this.tokenTimestamp == null) {
      return true;
    }
    else {
      let currentTime = new Date().getTime();
      let tokenTime = parseInt(this.tokenTimestamp);
      // let consider 90 seconds as the expiration time (2 minutes is real time)
      return currentTime - tokenTime > 90000;
    }
  }

  public renderRecaptcha(htmlElementId: string) {
    //check if grecaptcha is defined
    grecaptcha.render(htmlElementId, {
      'sitekey': this.environment.reCAPTCHA2SiteKey
    });
  }

  public isSuspicious() {
    return new Promise((resolve, reject) => {
      this.regenerateRecaptchaScore()
        .then(score => {
          resolve(score < 0.5);
        })
        .catch(error => {
          console.error('Failed to regenerate reCAPTCHA score:', error);
          resolve(false);
        });
    });
  }

  public regenerateRecaptchaScore(): Promise<number> {
    return new Promise((resolve, reject) => {
      if (typeof grecaptcha !== 'undefined') {
        grecaptcha.ready(() => {
          grecaptcha.execute(this.environment.reCAPTCHA3SiteKey, { action: 'page' }).then((token) => {
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            xhr.open('POST', this.environment.recaptchaServerUrl + '/recaptcha?token=' + token, true);
            xhr.onreadystatechange = () => {
              if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  if (response.score) {
                    // Return the score without storing it
                    resolve(response.score);
                  } else {
                    reject('No score in response');
                  }
                } catch (error) {
                  reject('Error parsing response: ' + error);
                }
              } else if (xhr.readyState === XMLHttpRequest.DONE) {
                reject('Request failed with status: ' + xhr.status);
              }
            };
            xhr.send();
          }).catch(error => {
            reject('reCAPTCHA execution error: ' + error);
          });
        });
      } else {
        reject('reCAPTCHA not loaded');
      }
    });
  }

  public validateRecaptcha() {
    return new Promise((resolve, reject) => {
      this.isSuspicious().then(isSuspicious => {
        if(isSuspicious) {
          let response = grecaptcha.getResponse();
          if (response.length === 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        }
        else {
          resolve(true)
        }
      });
    });
  }

  public showPopup() {
    if(this.componentRef == null) {
      // 1. Create factory for dynamic component
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(RecaptchaPopupComponent);

      // 2. Create component without container
      this.componentRef = componentFactory.create(this.injector);

      // 3. Attach component view to application to process lifecycle
      this.appRef.attachView(this.componentRef.hostView);

      // 4. Extract DOM element from component
      const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

      // 5. Insert element into DOM. For example, we add it to the body:
      document.body.appendChild(domElem);
    }

    // Call the show method and return its result
    return this.componentRef.instance.show();
  }


}
